import React, { useEffect, useRef, useState } from "react";
import { PartyStore } from "../../store/PartyStore.js";
import { EventBus } from "../../engine/EventBus.js";
import { getCombatManager } from "../../engine/combat/combatManagerRef.js";
import type { Character, PlayerCharacter } from "../../engine/types/entity.js";
import {
  CONSUMABLE_SLOTS_PER_CHARACTER,
  ConsumableSlotsStore,
} from "../../store/ConsumableSlotsStore.js";
import { InventoryStore } from "../../store/InventoryStore.js";
import type { EquipmentRecord } from "../../engine/types/items.js";
import { useLayout, type LayoutMode } from "../hooks/useLayout.js";
import { ItemIcon } from "./ItemIcon.js";
import { gameActor } from "../hooks/useGameMachine.js";
import { setPendingInventoryMember } from "../state/pendingInventoryOpen.js";
import { getSpellTargeting } from "../../engine/spells/spellTargetingRef.js";
import type { SpellTargetTeam } from "../../engine/types/spells.js";
import { damageColor, HEAL_COLOR } from "../damageColors.js";

/** EOB2-style portrait popup: element-colored damage splat, or heal burst. */
interface PortraitHit {
  readonly id: number;
  readonly memberId: string;
  readonly amount: number;
  readonly splat: string;
  readonly text: string;
  readonly heal: boolean;
}

let portraitHitSeq = 0;

const PORTRAIT_HIT_KEYFRAMES = `
@keyframes bk-portrait-hit {
  0%   { transform: translateY(6px) scale(0.6); opacity: 0; }
  12%  { transform: translateY(2px) scale(1.05); opacity: 1; }
  55%  { transform: translateY(-6px) scale(1); opacity: 1; }
  100% { transform: translateY(-16px) scale(1); opacity: 0; }
}`;

// One global keyframes rule shared by every layout mode's cards.
if (typeof document !== "undefined" && !document.getElementById("bk-portrait-hit-style")) {
  const styleEl = document.createElement("style");
  styleEl.id = "bk-portrait-hit-style";
  styleEl.textContent = PORTRAIT_HIT_KEYFRAMES;
  document.head.appendChild(styleEl);
}

interface ArmedAllyState {
  spellName: string;
  targeting: "single" | "multi" | "touch";
  targetTeam: SpellTargetTeam;
  minTargets: number;
  maxTargets: number;
  includeDead: boolean;
}

/**
 * HUD component — party member cards.
 *
 * Layout modes:
 * - `panel=true` (DesktopHUD): stacked vertically inside the host panel.
 * - mobile-portrait / mobile-landscape (U07): 2×2 grid. Front row
 *   (PartyStore indices 0, 1) on top, back row (2, 3) underneath.
 *   Portrait anchors the grid to the bottom of the viewport;
 *   landscape anchors it to the right edge as a column.
 * - desktop (non-panel fallback): stacked top-left — unused in the
 *   current App.tsx wiring but kept defensive.
 *
 * HP values refresh on `ui:party_update` / `combat:attack`; the ATB bar
 * polls the active CombatManager on every RAF tick so it stays smooth.
 */
interface PartyBarProps {
  panel?: boolean;
}

export function PartyBar({ panel = false }: PartyBarProps) {
  const [members, setMembers] = useState<readonly PlayerCharacter[]>(() =>
    PartyStore.getMembers(),
  );
  const [consumableMode, setConsumableMode] = useState(false);
  const [armedAlly, setArmedAlly] = useState<ArmedAllyState | null>(null);
  const [picked, setPicked] = useState<readonly string[]>([]);
  const [concentrators, setConcentrators] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );
  const barsRef = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const layoutMode = useLayout();
  const [portraitHits, setPortraitHits] = useState<readonly PortraitHit[]>([]);

  // Element-coded damage/heal numbers over the member's portrait —
  // enemy-side popups live in DamagePopups (world layer).
  useEffect(() => {
    let mounted = true;
    const push = (
      instanceId: string,
      amount: number,
      colors: { splat: string; text: string },
      heal: boolean,
    ): void => {
      if (amount <= 0) return;
      if (!PartyStore.getMember(instanceId)) return;
      const hit: PortraitHit = {
        id: portraitHitSeq++,
        memberId: instanceId,
        amount,
        splat: colors.splat,
        text: colors.text,
        heal,
      };
      setPortraitHits((prev) => [...prev, hit]);
      setTimeout(() => {
        if (mounted) setPortraitHits((prev) => prev.filter((h) => h.id !== hit.id));
      }, 950);
    };
    const off1 = EventBus.on("combat:damage_taken", ({ instanceId, amount, damageType }) => {
      push(instanceId, amount, damageColor(damageType), false);
    });
    const off2 = EventBus.on("combat:healed", ({ instanceId, amount }) => {
      push(instanceId, amount, HEAL_COLOR, true);
    });
    return () => {
      mounted = false;
      off1();
      off2();
    };
  }, []);

  useEffect(() => {
    const refresh = () => setMembers([...PartyStore.getMembers()]);
    const unsub1 = EventBus.on("ui:party_update", refresh);
    const unsub2 = EventBus.on("combat:attack", refresh);
    // S09 — track concentration per caster so the portrait can render an
    // outline + condition icon. Events are the single source of truth; the
    // UI never reads concentrationTracker directly.
    const unsub3 = EventBus.on("spell:concentration_started", ({ casterId, spellId }) => {
      setConcentrators((prev) => {
        const next = new Map(prev);
        next.set(casterId, spellId);
        return next;
      });
    });
    const unsub4 = EventBus.on("spell:concentration_ended", ({ casterId }) => {
      setConcentrators((prev) => {
        if (!prev.has(casterId)) return prev;
        const next = new Map(prev);
        next.delete(casterId);
        return next;
      });
    });
    const unsub5 = EventBus.on("ui:toggle_consumables", () => {
      setConsumableMode((v) => !v);
    });
    // S29 — ally-spell targeting via PartyBar portraits.
    const unsub6 = EventBus.on("ui:spell_armed", (e) => {
      // Only ally-team non-AoE spells route here. Enemy/AoE go to the
      // SpellTargetingBoard.
      if (e.targetTeam !== "ally") return;
      if (e.targeting === "aoe") return;
      setArmedAlly({
        spellName: e.spellName,
        targeting: e.targeting,
        targetTeam: e.targetTeam,
        minTargets: e.minTargets ?? 1,
        maxTargets: e.maxTargets ?? 1,
        includeDead: e.includeDead,
      });
      setPicked([]);
      gameActor.send({ type: "WORLD_FREEZE" });
    });
    const unsub7 = EventBus.on("ui:spell_disarmed", () => {
      setArmedAlly((prev) => {
        if (prev) gameActor.send({ type: "WORLD_UNFREEZE" });
        return null;
      });
      setPicked([]);
    });
    return () => {
      unsub1();
      unsub2();
      unsub3();
      unsub4();
      unsub5();
      unsub6();
      unsub7();
    };
  }, []);

  // Drive the ATB bar via RAF — avoids re-rendering the full PartyBar 60×/s.
  useEffect(() => {
    let raf = 0;
    const step = () => {
      const cm = getCombatManager();
      for (const m of PartyStore.getMembers()) {
        const bar = barsRef.current.get(m.instanceId);
        if (!bar) continue;
        let pct = 100;
        if (cm) {
          const atb = cm.getPlayerAtbState(m.instanceId);
          pct = atb.duration > 0
            ? Math.max(0, Math.min(100, (1 - atb.remaining / atb.duration) * 100))
            : 100;
        }
        bar.style.width = `${pct}%`;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  if (members.length === 0) return null;

  const renderActionRow = (m: PlayerCharacter) => {
    const eq: EquipmentRecord = InventoryStore.get(m.instanceId).equipment;
    const mainItem = eq.mainHand;
    const offItem = eq.offHand;
    const isPassive = (item: typeof mainItem) =>
      item !== null && (item.tags.includes("passive") || item.tags.includes("armor"));

    // Square, portrait-sized touch targets. ~48px is the Material/Apple HIG
    // floor for fingertip taps; we use aspect-ratio so they scale with card width.
    const slotBase: React.CSSProperties = {
      flex: 1,
      aspectRatio: "1 / 1",
      minHeight: 44,
      padding: 0,
      border: "1px solid rgba(255,255,255,0.18)",
      borderRadius: 4,
      pointerEvents: "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    };

    const handStyle = (item: typeof mainItem, passive: boolean): React.CSSProperties => ({
      ...slotBase,
      background: item ? (passive ? "#2a2a3a" : "#253040") : "rgba(0,0,0,0.3)",
      color: item ? (passive ? "#888" : "#c8e6ff") : "#555",
      cursor: item && !passive ? "pointer" : "default",
      fontSize: 10,
      fontFamily: "monospace",
    });

    const handLabel = (item: typeof mainItem) => {
      if (!item) return <span aria-hidden>—</span>;
      return <ItemIcon itemId={item.id} name={item.name} size="fill" />;
    };

    const handleHand = (hand: "mainHand" | "offHand") => {
      const item = hand === "mainHand" ? mainItem : offItem;
      if (!item || isPassive(item)) return;
      EventBus.emit("ui:hand_action", { characterId: m.instanceId, hand });
    };

    const mainPassive = isPassive(mainItem);
    const offPassive = isPassive(offItem);

    const dead = m.hp <= 0;
    const allyEligible =
      armedAlly !== null && (armedAlly.includeDead ? dead : !dead);
    const allyArmed = armedAlly !== null;
    const allySelected = picked.includes(m.instanceId);
    const portraitStyle: React.CSSProperties = {
      ...slotBase,
      position: "relative",
      background: dead ? "#3a1010" : "#1a1d24",
      borderColor: allyArmed
        ? allySelected
          ? "#ffd54a"
          : allyEligible
            ? "rgba(120, 200, 255, 0.95)"
            : "rgba(120,120,120,0.4)"
        : dead
          ? "rgba(255,80,80,0.5)"
          : "rgba(255,255,255,0.18)",
      boxShadow: allyArmed && allyEligible && !allySelected
        ? "0 0 6px 1px rgba(120, 200, 255, 0.6)"
        : undefined,
      opacity: allyArmed && !allyEligible ? 0.45 : 1,
      cursor: allyArmed ? (allyEligible ? "pointer" : "not-allowed") : "pointer",
    };
    const deadImgFilter = dead
      ? "grayscale(0.7) sepia(0.4) hue-rotate(-30deg) saturate(2) brightness(0.55)"
      : undefined;
    const portrait = (m as Partial<Character> & PlayerCharacter).portrait ?? null;
    const handlePortrait = () => {
      // S29 — while an ally spell is armed, portraits become target picks
      // and the normal inventory routing is suppressed.
      if (armedAlly) {
        if (!allyEligible) return;
        const dispatcher = getSpellTargeting();
        if (!dispatcher) return;
        if (armedAlly.targeting === "multi") {
          const has = picked.includes(m.instanceId);
          const next = has
            ? picked.filter((i) => i !== m.instanceId)
            : [...picked, m.instanceId];
          setPicked(next);
          dispatcher.commitOnEntity({ instanceId: m.instanceId });
          if (!has && next.length >= armedAlly.maxTargets) {
            queueMicrotask(() => dispatcher.finalize());
          }
          return;
        }
        dispatcher.commitOnEntity({ instanceId: m.instanceId });
        return;
      }
      setPendingInventoryMember(m.instanceId);
      gameActor.send({ type: "OPEN_MENU" });
    };

    return (
      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
        <button
          type="button"
          data-testid={`portrait-${m.instanceId}`}
          title={`${m.name} — open inventory`}
          aria-label={`Open ${m.name}'s inventory`}
          style={portraitStyle}
          onClick={handlePortrait}
        >
          {portrait ? (
            <img
              src={portrait}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                ...(deadImgFilter ? { filter: deadImgFilter } : {}),
              }}
            />
          ) : (
            <span style={{ color: "#888", fontSize: 14, fontFamily: "monospace" }}>
              {m.name.slice(0, 1).toUpperCase()}
            </span>
          )}
          {portraitHits
            .filter((h) => h.memberId === m.instanceId)
            .map((h) => (
              <span
                key={h.id}
                data-testid="portrait-damage-popup"
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: "bk-portrait-hit 0.95s ease-out forwards",
                  pointerEvents: "none",
                }}
              >
                <svg width={34} height={30} viewBox="0 0 46 40" style={{ position: "absolute" }}>
                  {h.heal ? (
                    <g fill={h.splat} opacity={0.9}>
                      <circle cx={23} cy={12} r={7} />
                      <circle cx={33} cy={17} r={7} />
                      <circle cx={33} cy={26} r={7} />
                      <circle cx={23} cy={30} r={7} />
                      <circle cx={13} cy={26} r={7} />
                      <circle cx={13} cy={17} r={7} />
                      <circle cx={23} cy={21} r={10} />
                    </g>
                  ) : (
                    <path
                      d="M23 3 L28 10 L37 6 L33 15 L43 18 L34 23 L39 33 L29 28 L24 38 L19 29 L9 34 L13 24 L3 20 L12 15 L8 6 L17 10 Z"
                      fill={h.splat}
                      opacity={0.92}
                    />
                  )}
                </svg>
                <span
                  style={{
                    position: "relative",
                    color: h.text,
                    fontFamily: "monospace",
                    fontWeight: 700,
                    fontSize: 13,
                    textShadow: "0 1px 2px rgba(0,0,0,0.9)",
                  }}
                >
                  {h.heal ? `+${h.amount}` : h.amount}
                </span>
              </span>
            ))}
        </button>
        <button
          type="button"
          data-testid={`hand-main-${m.instanceId}`}
          title={mainItem?.name ?? "Empty main hand"}
          style={handStyle(mainItem, mainPassive)}
          onClick={() => handleHand("mainHand")}
        >
          {handLabel(mainItem)}
        </button>
        <button
          type="button"
          data-testid={`hand-off-${m.instanceId}`}
          title={offItem?.name ?? "Empty off hand"}
          style={handStyle(offItem, offPassive)}
          onClick={() => handleHand("offHand")}
        >
          {handLabel(offItem)}
        </button>
      </div>
    );
  };

  const renderCard = (m: PlayerCharacter) => {
    const hpPct = Math.max(0, m.hp / m.maxHp) * 100;
    const hpColor = hpPct > 50 ? "#4caf50" : hpPct > 25 ? "#ff9800" : "#f44336";
    const concentrationSpell = concentrators.get(m.instanceId);
    const concentrating = concentrationSpell !== undefined;
    return (
      <div
        key={m.instanceId}
        data-testid={`party-portrait-${m.instanceId}`}
        data-concentrating={concentrating ? "true" : "false"}
        style={{
          background: "rgba(0,0,0,0.65)",
          border: concentrating
            ? "1px solid #b48df8"
            : "1px solid rgba(255,255,255,0.2)",
          boxShadow: concentrating
            ? "0 0 6px 1px rgba(180, 141, 248, 0.75)"
            : undefined,
          borderRadius: 4,
          padding: "4px 8px",
          minWidth: 0,
        }}
      >
        <div
          style={{
            color: "white",
            fontSize: 11,
            fontFamily: "monospace",
            marginBottom: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {m.name}
            {concentrating && (
              <span
                data-testid={`concentration-icon-${m.instanceId}`}
                title={`Concentrating on ${concentrationSpell}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  border: "1px solid #b48df8",
                  color: "#b48df8",
                  fontSize: 9,
                  fontWeight: "bold",
                  lineHeight: 1,
                }}
              >
                C
              </span>
            )}
          </span>
          <span style={{ color: "#ccc", fontFamily: "monospace" }}>
            {m.hp} / {m.maxHp}
          </span>
        </div>
        {/* HP bar */}
        <div
          style={{
            background: "#333",
            borderRadius: 2,
            height: 6,
            width: "100%",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: hpColor,
              height: "100%",
              width: `${hpPct}%`,
              transition: "width 0.2s ease",
            }}
          />
        </div>
        {consumableMode && (
          <div
            data-testid={`consumable-slots-${m.instanceId}`}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${CONSUMABLE_SLOTS_PER_CHARACTER}, 1fr)`,
              gap: 3,
              marginTop: 4,
            }}
          >
            {ConsumableSlotsStore.getBindings(m.instanceId).map((binding, i) => (
              <div
                key={i}
                title={binding ?? "Empty"}
                style={{
                  height: 18,
                  border: "1px solid rgba(255,255,255,0.25)",
                  borderRadius: 2,
                  background: binding ? "#394150" : "rgba(0,0,0,0.45)",
                  color: "#ddd",
                  fontSize: 9,
                  fontFamily: "monospace",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {binding ? binding.slice(0, 3) : ""}
              </div>
            ))}
          </div>
        )}
        {/* Portrait + main / off hand — square touch targets */}
        {renderActionRow(m)}
        {/* ATB cooldown bar — 100% fill = ready to act. */}
        <div
          data-testid={`atb-track-${m.instanceId}`}
          style={{
            background: "#222",
            borderRadius: 2,
            height: 3,
            width: "100%",
            overflow: "hidden",
            marginTop: 3,
          }}
        >
          <div
            ref={(el) => {
              barsRef.current.set(m.instanceId, el);
            }}
            data-testid={`atb-fill-${m.instanceId}`}
            style={{
              background: "#7dcfff",
              height: "100%",
              width: "100%",
            }}
          />
        </div>
      </div>
    );
  };

  const dispatcher = getSpellTargeting();
  const onConfirmAlly = () => {
    if (!armedAlly || armedAlly.targeting !== "multi") return;
    if (picked.length < armedAlly.minTargets) return;
    dispatcher?.finalize();
  };
  const onCancelAlly = () => {
    dispatcher?.cancel();
    EventBus.emit("ui:spell_disarmed", { reason: "cancelled" });
  };

  // Panel mode (DesktopHUD) or desktop non-panel fallback: keep the
  // stacked layout. Cards are card-min-width = 160 so the desktop
  // panel renders them uniformly.
  const stackedMode =
    panel || (layoutMode === "desktop" && !isMobile(layoutMode));
  if (stackedMode) {
    return (
      <>
        {armedAlly && <AllyTargetingDim />}
        <div
          style={
            panel
              ? { padding: 12, display: "flex", flexDirection: "column", gap: 6 }
              : {
                  position: "absolute",
                  top: 12,
                  left: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  pointerEvents: "none",
                }
          }
        >
          {armedAlly && (
            <AllyTargetingHeader
              armed={armedAlly}
              picked={picked}
              onConfirm={onConfirmAlly}
              onCancel={onCancelAlly}
            />
          )}
          {members.map((m) => (
            <div key={m.instanceId} style={{ minWidth: 160 }}>
              {renderCard(m)}
            </div>
          ))}
        </div>
      </>
    );
  }

  // Mobile 2×2 grid. Slots are rendered even when empty so the 2×2
  // shape is stable as party size shrinks (e.g. after a death).
  const frontRow = PartyStore.getFrontRow();
  const backRow = PartyStore.getBackRow();
  const slot = (row: readonly PlayerCharacter[], idx: number) => {
    const m = row[idx];
    if (!m) return <EmptySlot key={`empty-${idx}`} />;
    return renderCard(m);
  };

  const containerStyle: React.CSSProperties =
    layoutMode === "mobile-landscape"
      ? {
          position: "absolute",
          top: 8,
          right: 8,
          bottom: 8,
          width: "30%",
          maxWidth: 360,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          pointerEvents: "none",
        }
      : {
          // Pin the grid directly under the NavCluster (which sits at
          // top: calc(50dvh + 8px) and is 56px tall, with a 4px gap below).
          // Anchoring `top` instead of `bottom` removes the dead space
          // between the nav strip and the cards on tall phones.
          position: "absolute",
          left: 8,
          right: 8,
          top: "calc(50dvh + 76px)",
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 4px)",
          display: "flex",
          flexDirection: "column",
          gap: 6,
          pointerEvents: "none",
        };

  return (
    <>
      {armedAlly && <AllyTargetingDim />}
      <div data-component="party-bar" data-layout-mode={layoutMode} style={containerStyle}>
        {armedAlly && (
          <AllyTargetingHeader
            armed={armedAlly}
            picked={picked}
            onConfirm={onConfirmAlly}
            onCancel={onCancelAlly}
          />
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gridTemplateRows: "auto auto",
            gap: 6,
            pointerEvents: "none",
          }}
        >
          {slot(frontRow, 0)}
          {slot(frontRow, 1)}
          {slot(backRow, 0)}
          {slot(backRow, 1)}
        </div>
      </div>
    </>
  );
}

function AllyTargetingDim(): React.ReactElement {
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        pointerEvents: "none",
        zIndex: 5,
      }}
    />
  );
}

function AllyTargetingHeader({
  armed,
  picked,
  onConfirm,
  onCancel,
}: {
  armed: ArmedAllyState;
  picked: readonly string[];
  onConfirm: () => void;
  onCancel: () => void;
}): React.ReactElement {
  const text =
    armed.targeting === "multi"
      ? `${armed.spellName} · ${picked.length}/${armed.maxTargets}`
      : `Which ally will you target? · ${armed.spellName}`;
  const confirmEnabled =
    armed.targeting === "multi" && picked.length >= armed.minTargets;
  return (
    <div
      data-testid="ally-targeting-header"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        padding: "6px 10px",
        marginBottom: 6,
        background: "rgba(20, 24, 32, 0.95)",
        border: "1px solid rgba(120, 200, 255, 0.6)",
        borderRadius: 4,
        color: "#cfe6ff",
        fontFamily: "monospace",
        fontSize: 12,
        pointerEvents: "auto",
      }}
    >
      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>{text}</span>
      {armed.targeting === "multi" && (
        <button
          type="button"
          onClick={onConfirm}
          disabled={!confirmEnabled}
          style={{
            padding: "3px 8px",
            background: confirmEnabled ? "#2a5a8a" : "#333",
            color: confirmEnabled ? "#cfe6ff" : "#666",
            border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: 3,
            cursor: confirmEnabled ? "pointer" : "default",
            fontFamily: "monospace",
            fontSize: 11,
          }}
        >
          Confirm
        </button>
      )}
      <button
        type="button"
        onClick={onCancel}
        style={{
          padding: "3px 6px",
          background: "transparent",
          color: "#ccc",
          border: "1px solid rgba(255,255,255,0.25)",
          borderRadius: 3,
          cursor: "pointer",
          fontFamily: "monospace",
          fontSize: 11,
        }}
      >
        ✕
      </button>
    </div>
  );
}

function isMobile(mode: LayoutMode): boolean {
  return mode === "mobile-portrait" || mode === "mobile-landscape";
}

function EmptySlot(): React.ReactElement {
  return (
    <div
      aria-hidden
      style={{
        background: "rgba(0,0,0,0.25)",
        border: "1px dashed rgba(255,255,255,0.1)",
        borderRadius: 4,
        minHeight: 54,
      }}
    />
  );
}
